import type { CustomerDebt, DebtOrderItem } from '@/lib/api/customer-api'

// Một đơn còn nợ dùng cho preview phân bổ. `sales_order_id` bắt buộc để map vào
// `allocations` khi gửi lên; đơn nào thiếu ref không tham gia phân bổ.
export interface OutstandingOrder {
    sales_order_id: string
    remaining_amount: number
    created_at: string
    order_code?: string
    items?: DebtOrderItem[]
}

export interface AllocationLine extends OutstandingOrder {
    // Số tiền phiếu thu rải vào đơn này.
    allocated: number
}

export interface FifoAllocation {
    lines: AllocationLine[]
    // Phần dư không map vào đơn nào → sẽ thành credit (nợ âm).
    leftover: number
}

// Lọc + sắp các đơn còn nợ theo FIFO (cũ → mới). Loại đơn không có
// sales_order_id (không thể phân bổ) và đơn đã trả hết.
export function toOutstandingOrders(debts: CustomerDebt[]): OutstandingOrder[] {
    return debts
        .filter((d) => !!d.sales_order_id && d.remaining_amount > 0)
        .map((d) => ({
            sales_order_id: d.sales_order_id as string,
            remaining_amount: d.remaining_amount,
            created_at: d.created_at,
            order_code: d.order_code,
            items: d.items,
        }))
        .sort(
            (a, b) =>
                new Date(a.created_at).getTime() -
                new Date(b.created_at).getTime(),
        )
}

// Rải `amount` vào danh sách đơn còn nợ theo FIFO. Trả từng dòng đã phân bổ +
// phần dư (leftover) trở thành credit. Thuần tuý — không mutate input.
export function computeFifoAllocation(
    orders: OutstandingOrder[],
    amount: number,
): FifoAllocation {
    let remaining = amount > 0 ? amount : 0
    const lines: AllocationLine[] = orders.map((o) => {
        const allocated = Math.min(remaining, o.remaining_amount)
        remaining -= allocated
        return { ...o, allocated }
    })
    return { lines, leftover: remaining }
}
